import { Group } from '@/components/_common';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import {
  WorkOrderItem,
  WorkOrderItemOrigin,
  WorkOrderItemStockStatus,
  WorkOrderItemType,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { Button, Checkbox, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useIntl } from 'react-intl';
import { memo, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import formatterPrice from '@/utils/number/formatterPrice';
import WorkOrderItemCommision from './WorkOrderItemCommision';
import WorkOrderItemDetail from './WorkOrderItemDetail';
import WorkOrderItemRemove from './WorkOrderItemRemove';
import WorkOrderItemEdit from './WorkOrderItemEdit';
import WorkOrderItemAddKit from './WorkOrderItemAddKit';
import WorkOrderItemChangeStatus from './WorkOrderItemChangeStatus';
import WorkOrderItemTypeTag from './WorkOrderItemTypeTag';
import WorkOrderItemChangePart from './WorkOrderItemChangePart';
import WorkOrderItemQuantity from './WorkOrderItemQuantity';
import WorkOrderItemUnitPrice from './WorkOrderItemUnitPrice';
import WorkOrderItemDiscount from './WorkOrderItemDiscount';
import WorkOrderItemDiscountValue from './WorkOrderItemDiscountValue';

import './style.less';
import WorkOrderItemBenefitCostAlert from './WorkOrderItemBenefitCostAlert';
import { isAppPortocar } from '@/index';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import WorkOrderItemBatchRemove from './WorkOrderItemBatchRemove';
import { useDisclosure } from '@/hooks';

export type WorkOrderItemData = WorkOrderItem & {
  groupDiscountValue?: number;
  groupTotalValue?: number;
  groupStatus?: WorkOrderItemStockStatus[];
  disableDelete?: boolean;
  disableEdit?: boolean;
  disableChangePart?: boolean;
  disableUnitPrice?: boolean;
  children?: (WorkOrderItem & {
    disableDelete?: boolean;
    disableEdit?: boolean;
    disableChangePart?: boolean;
    disableUnitPrice?: boolean;
  })[];
};

interface Props {
  workOrderItems?: WorkOrderItem[];
  loading: boolean;
  refetchItems: VoidFunction;
}

function WorkOrderItemList({
  workOrderItems,
  loading,
  refetchItems: refetch,
}: Props) {
  const intl = useIntl();
  const {
    workorder: { data: workorder },
    items: { selectedItemsRows, setSelectedItemsRows },
  } = useWorkorderDetailContext();

  const history = useHistory();
  const itemHashHighLight = history.location.hash;

  const [hideServicesZeroed, setHideServicesZeroed] = useDisclosure();
  const [hideSuppliedParts, setHideSuppliedParts] = useDisclosure();

  const isPortoBattery =
    isAppPortocar &&
    (!!workorder?.isPortoBatteryWorkOrder || !!workorder?.portoAssistantCode);

  useEffect(() => {
    if (history.location.hash && !loading) {
      setTimeout(() => {
        history.replace(history.location.pathname);
      }, 4000);
    }
  }, [history.location.hash, loading]);

  const [totalItems, setTotalItems] = useState(10);

  const tableData = useMemo<WorkOrderItemData[]>(
    () =>
      workOrderItems?.reduce((acc, curr) => {
        const isBenefitClosed = !!curr.benefitDateUsed;

        if (
          isAppPortocar &&
          curr.portoTaskId &&
          acc.some((item) => item.portoTaskId === curr.portoTaskId)
        ) {
          return acc.map((item) => {
            if (item.portoTaskId === curr.portoTaskId) {
              return {
                ...item,
                children: [...(item.children || []), curr],
                haveCommission: curr.haveCommission || item.haveCommission,
                quantity: item.quantity + curr.quantity,
                discount: item.discount + curr.discount,
                unitPrice: item.unitPrice + curr.unitPrice,
                groupDiscountValue:
                  curr.quantity * curr.unitPrice * curr.discount +
                  (item.groupDiscountValue || 0),
                groupTotalValue:
                  curr.quantity * curr.unitPrice -
                  curr.quantity * curr.unitPrice * curr.discount +
                  (item.groupTotalValue || 0),
              };
            }
            return item;
          });
        }

        if (curr.kitId && acc.some((item) => item.kitId === curr.kitId)) {
          return acc.map((item) => {
            if (item.kitId === curr.kitId) {
              return {
                ...item,
                children: [...(item.children || []), curr],
                haveCommission: curr.haveCommission || item.haveCommission,
                quantity: item.quantity + curr.quantity,
                discount: item.discount + curr.discount,
                unitPrice: item.unitPrice + curr.unitPrice,
                groupDiscountValue:
                  curr.quantity * curr.unitPrice * curr.discount +
                  (item.groupDiscountValue || 0),
                groupTotalValue:
                  curr.quantity * curr.unitPrice -
                  curr.quantity * curr.unitPrice * curr.discount +
                  (item.groupTotalValue || 0),
              };
            }
            return item;
          });
        }

        if (
          curr.benefitServiceCode &&
          acc.some(
            (item) => item.benefitServiceCode === curr.benefitServiceCode,
          )
        ) {
          return acc.map((item) => {
            if (item.benefitServiceCode === curr.benefitServiceCode) {
              return {
                ...item,
                children: [
                  ...(item.children || []),
                  {
                    ...curr,
                    disableChangePart: true,
                    disableUnitPrice: true,
                    disableDelete: isBenefitClosed,
                    disableEdit: isBenefitClosed,
                  },
                ],
                id: curr.type === WorkOrderItemType.Service ? curr.id : item.id,
                haveCommission: curr.haveCommission || item.haveCommission,
                quantity: item.quantity + curr.quantity,
                discount: item.discount + curr.discount,
                unitPrice: item.unitPrice + curr.unitPrice,
                groupDiscountValue:
                  curr.quantity * curr.unitPrice * curr.discount +
                  (item.groupDiscountValue || 0),
                groupTotalValue:
                  curr.quantity * curr.unitPrice -
                  curr.quantity * curr.unitPrice * curr.discount +
                  (item.groupTotalValue || 0),
                disableEdit: true,
                disableChangePart: true,
                disableDelete: isBenefitClosed,
              };
            }
            return item;
          });
        }

        if (
          curr.groupingId &&
          acc.some((item) => item.groupingId === curr.groupingId)
        ) {
          return acc.map((item) => {
            if (item.groupingId === curr.groupingId) {
              return {
                ...item,
                children: [...(item.children || []), curr],
                haveCommission: curr.haveCommission || item.haveCommission,
                quantity: item.quantity + curr.quantity,
                discount: item.discount + curr.discount,
                unitPrice: item.unitPrice + curr.unitPrice,
                groupDiscountValue:
                  curr.quantity * curr.unitPrice * curr.discount +
                  (item.groupDiscountValue || 0),
                groupTotalValue:
                  curr.quantity * curr.unitPrice -
                  curr.quantity * curr.unitPrice * curr.discount +
                  (item.groupTotalValue || 0),
              };
            }
            return item;
          });
        }

        if (curr.portoTaskId && isAppPortocar) {
          return [
            ...acc,
            {
              ...curr,
              id: curr.portoTaskId,
              description: String(curr.description),
              sku: '',
              type: WorkOrderItemType.Porto,
              groupDiscountValue:
                curr.quantity * curr.unitPrice * curr.discount,
              groupTotalValue:
                curr.quantity * curr.unitPrice -
                curr.quantity * curr.unitPrice * curr.discount,
              disableEdit: true,
              disableDelete: true,
              children: [{ ...curr }],
            },
          ];
        }

        if (curr.kitId) {
          return [
            ...acc,
            {
              ...curr,
              id: curr.kitId,
              description: String(curr.kitName),
              sku: '',
              type: WorkOrderItemType.Kit,
              groupDiscountValue:
                curr.quantity * curr.unitPrice * curr.discount,
              groupTotalValue:
                curr.quantity * curr.unitPrice -
                curr.quantity * curr.unitPrice * curr.discount,
              children: [{ ...curr }],
            },
          ];
        }

        if (curr.benefitServiceCode) {
          return [
            ...acc,
            {
              ...curr,
              description: String(curr.benefitName),
              sku: '',
              type: WorkOrderItemType.Benefit,
              groupDiscountValue:
                curr.quantity * curr.unitPrice * curr.discount,
              groupTotalValue:
                curr.quantity * curr.unitPrice -
                curr.quantity * curr.unitPrice * curr.discount,
              disableEdit: true,
              disableChangePart: true,
              disableDelete: isBenefitClosed,
              children: [
                {
                  ...curr,
                  disableChangePart: true,
                  disableUnitPrice: true,
                  disableDelete: isBenefitClosed,
                  disableEdit: isBenefitClosed,
                },
              ],
            },
          ];
        }

        if (curr.groupingId) {
          return [
            ...acc,
            {
              ...curr,
              description: String(curr.groupingName),
              sku: '',
              type: WorkOrderItemType.Bosch,
              groupDiscountValue:
                curr.quantity * curr.unitPrice * curr.discount,
              groupTotalValue:
                curr.quantity * curr.unitPrice -
                curr.quantity * curr.unitPrice * curr.discount,
              disableEdit: true,
              disableDelete: true,
              children: [{ ...curr }],
            },
          ];
        }

        return [...acc, curr];
      }, [] as WorkOrderItemData[]) || [],
    [workOrderItems],
  );

  const tableDataPaginated = useMemo(() => {
    let data = tableData.slice(0, totalItems);

    if (hideServicesZeroed) {
      data = data.filter((item) => item.unitPrice > 0);
    }

    if (hideSuppliedParts) {
      data = data.filter(
        (item) => item.origin !== WorkOrderItemOrigin.Supplied,
      );
    }

    return data;
  }, [tableData, totalItems, hideServicesZeroed, hideSuppliedParts]);

  const hasNextItems = useMemo(
    () => totalItems < tableData.length,
    [tableData, totalItems],
  );

  const columnsConfig = useConfigurationColumnsPart({
    internalCode: 'sku',
    isSorter: false,
    widthColumns: 100,
  });

  const columns: ColumnsType<WorkOrderItemData> = [
    columnsConfig,
    {
      dataIndex: 'type',
      title: intl.formatMessage({
        id: 'workOrders.domain.items.type',
      }),
      width: 150,
      ellipsis: true,
      render: (_, item) => <WorkOrderItemTypeTag workOrderItem={item} />,
    },
    {
      dataIndex: 'status',
      title: intl.formatMessage({
        id: 'workOrders.domain.items.status',
      }),
      width: 50,
      ellipsis: true,
      render: (_, item) => <WorkOrderItemChangeStatus workOrderItem={item} />,
    },
    {
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'workOrders.domain.items.description',
      }),
      width: 200,
      render: (value, item) => (
        <EllipsisHoverToggle
          style={
            item.type === WorkOrderItemType.Kit ||
            item.type === WorkOrderItemType.Benefit ||
            item.type === WorkOrderItemType.Bosch
              ? { fontWeight: 'bold' }
              : {}
          }
        >
          {value}
        </EllipsisHoverToggle>
      ),
    },
    {
      dataIndex: 'quantity',
      width: COLUMNS_WIDTH.action1,
      ellipsis: true,
      title: intl.formatMessage({
        id: 'workOrders.domain.items.quantityTime',
      }),
      align: 'right',
      render: (_, item) => <WorkOrderItemQuantity item={item} />,
    },
    {
      dataIndex: 'unitPrice',
      title: intl.formatMessage({
        id: 'workOrders.domain.items.unitPrice',
      }),
      width: COLUMNS_WIDTH.action1,
      ellipsis: true,
      align: 'right',
      render: (_, item) => <WorkOrderItemUnitPrice item={item} />,
    },
    {
      dataIndex: 'discount',
      title: intl.formatMessage({
        id: 'workOrders.domain.items.discount',
      }),
      width: COLUMNS_WIDTH.action1,
      ellipsis: true,
      align: 'right',
      render: (_, item) => {
        return <WorkOrderItemDiscount item={item} />;
      },
    },
    {
      title: intl.formatMessage({
        id: 'workOrders.domain.items.discountValue',
      }),
      align: 'right',
      width: COLUMNS_WIDTH.action1,
      ellipsis: true,
      render: (_, item) => {
        const isGroup =
          item.type === WorkOrderItemType.Kit ||
          item.type === WorkOrderItemType.Benefit ||
          item.type === WorkOrderItemType.Bosch;

        if (isGroup)
          return (
            <span style={{ fontWeight: 'bold' }}>
              {formatterPrice(item.groupDiscountValue || 0)}
            </span>
          );

        return <WorkOrderItemDiscountValue item={item} />;
      },
    },
    {
      title: intl.formatMessage({
        id: 'workOrders.domain.items.totalValue',
      }),
      align: 'right',
      width: COLUMNS_WIDTH.price,
      ellipsis: true,
      render: (_, item) => {
        const isGroup =
          item.type === WorkOrderItemType.Kit ||
          item.type === WorkOrderItemType.Benefit ||
          item.type === WorkOrderItemType.Bosch;

        return (
          <span style={isGroup ? { fontWeight: 'bold' } : {}}>
            {formatterPrice(
              isGroup
                ? item.groupTotalValue || 0
                : item.quantity * item.unitPrice -
                    item.quantity * item.unitPrice * item.discount,
            )}
          </span>
        );
      },
    },
    {
      title: intl.formatMessage({
        id: 'commons.list.header.action',
      }),
      width: 200,
      align: 'right',
      fixed: 'right',
      render: (_, item) => {
        const isDisabled = !!itemHashHighLight;

        return (
          <Group
            gap={2}
            position="right"
            style={{
              pointerEvents: isDisabled ? 'none' : 'auto',
              opacity: isDisabled ? 0.5 : 1,
            }}
          >
            <WorkOrderItemBenefitCostAlert workOrderItem={item} />
            <WorkOrderItemCommision workOrderItem={item} />
            <WorkOrderItemDetail workOrderItem={item} />
            <WorkOrderItemAddKit refetchItems={refetch} workOrderItem={item} />
            <WorkOrderItemEdit workOrderItem={item} />
            <WorkOrderItemChangePart workOrderItem={item} />
            <WorkOrderItemRemove workOrderItem={item} />
          </Group>
        );
      },
    },
  ];
  const rowKey = (row: WorkOrderItemData) => `${row.type}-${row.id}`;

  const isItemDisabled = (item: WorkOrderItemData) =>
    item.disableDelete ||
    !hasPermissions([Authorities.MOVIMENTATION_WORKORDER_DELETE_ITEM]) ||
    (isAppPortocar && !!item.portoTaskId) ||
    isPortoBattery;

  function updateSelectedRows(keys: string[]) {
    setSelectedItemsRows(
      tableData
        .flatMap((item) => [item, ...(item.children ?? [])])
        .filter((item) => keys.includes(rowKey(item))),
    );
  }

  const selectedRowKeys = useMemo(
    () => selectedItemsRows.map((item) => rowKey(item)),
    [selectedItemsRows],
  );

  const rowSelection = {
    selectedRowKeys,

    onSelect: (record: WorkOrderItemData, selected: boolean) => {
      const key = rowKey(record);
      const enabledChildrenKeys = (record.children ?? [])
        .filter((child) => !isItemDisabled(child))
        .map(rowKey);

      const relatedKeys = [key, ...enabledChildrenKeys];

      const finalKeys = selected
        ? [
            ...selectedRowKeys,
            ...relatedKeys.filter((key) => !selectedRowKeys.includes(key)),
          ]
        : selectedRowKeys.filter((key) => !relatedKeys.includes(key));

      updateSelectedRows(finalKeys);
    },

    onSelectAll: (selected: boolean, selectedRows: WorkOrderItemData[]) => {
      if (selected) {
        const enabledKeys = selectedRows.flatMap((row) => {
          const keys: string[] = [];

          if (!isItemDisabled(row)) {
            keys.push(rowKey(row));
          }

          if (row.children?.length) {
            row.children.forEach((child) => {
              if (!isItemDisabled(child)) {
                keys.push(rowKey(child));
              }
            });
          }

          return keys;
        });

        updateSelectedRows(enabledKeys);
      } else {
        updateSelectedRows([]);
      }
    },

    getCheckboxProps: (record: WorkOrderItemData) => ({
      disabled: isItemDisabled(record),
    }),
  };

  return (
    <>
      <WorkOrderItemBatchRemove />

      <Checkbox
        value={hideServicesZeroed}
        onChange={setHideServicesZeroed.toggle}
      >
        Ocultar itens zerados
      </Checkbox>

      <Checkbox
        value={hideSuppliedParts}
        onChange={setHideSuppliedParts.toggle}
      >
        Ocultar peças fornecidas
      </Checkbox>

      <Table
        columns={columns}
        loading={loading}
        dataSource={tableDataPaginated}
        rowKey={rowKey}
        scroll={{ x: getTableScrollX(columns) }}
        pagination={false}
        key={itemHashHighLight}
        rowClassName={(row) =>
          `#item-${row.id}` === itemHashHighLight ? 'item-hight-light' : ''
        }
        rowSelection={rowSelection}
      />

      <Group position="center" style={{ marginTop: 24 }}>
        <Button
          disabled={!hasNextItems}
          onClick={() =>
            setTotalItems((prev) =>
              prev < tableData.length ? prev + 10 : prev,
            )
          }
        >
          Carregar mais
        </Button>
      </Group>
    </>
  );
}

export default memo(WorkOrderItemList);
